import { Link, useParams } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  sortingFns,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  CheckCircle2,
  Download,
  ListFilter,
  Search,
  UploadCloud,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { Loading, Spinner } from '@/components/shared';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button as NewButton } from '@/components/ui/NewButton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useMatter from '@/hooks/useMatter';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { formatSmartDate } from '@/utils/date-utils';
import { removeTimestampPrefix } from '@/utils/file-utils';

type MatterUpload = {
  guid: string;
  batch_guid: string;
  file_name: string;
  display_name: string | null;
  destination_folder: string | null;
  destination_folder_name: string | null;
  processing_status: string;
  error_message: string | null;
  created_at: string;
  uploaded_by_name: string | null;
  source_type: string;
  source_label: string;
  file_reference_guid: string | null;
};

type FilterOption = {
  value: string;
  label: string;
  count: number;
};

type MatterUploadsResponse = {
  uploads: MatterUpload[];
  total: number;
  filter_options?: UploadFilterOptionsResponse;
};

type UploadFilterOptionsResponse = {
  statuses: FilterOption[];
  sources: FilterOption[];
  uploaded_by: FilterOption[];
};

type FilterPopoverId = 'uploaded_by' | 'source' | 'status';

const STATUS_LABELS: Record<string, string> = {
  uploading: 'Uploading',
  uploaded: 'Processing',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
};

const SORTABLE_COLUMN_IDS = new Set([
  'filename',
  'destination_folder',
  'status',
  'created_at',
  'uploaded_by',
  'source',
]);

const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_UPLOAD_FILTER_OPTIONS: UploadFilterOptionsResponse = {
  statuses: [],
  sources: [],
  uploaded_by: [],
};

const statusBadgeClassName = (status: string) => {
  switch (status) {
    case 'completed':
      return 'border-emerald-100 bg-emerald-50/40 text-emerald-800/60';
    case 'failed':
      return 'border-amber-100 bg-amber-50/40 text-amber-800/60';
    case 'processing':
    case 'uploaded':
      return 'border-sky-100 bg-sky-50/40 text-sky-800/60';
    case 'uploading':
      return 'border-slate-200 bg-slate-50/40 text-slate-500';
    default:
      return 'border-slate-200 bg-slate-50/40 text-slate-500';
  }
};

const downloadBlob = (filename: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const safeFilenamePart = (value: string | null | undefined, fallback: string) => {
  const cleaned = Array.from(value || '', (char) => {
    const charCode = char.charCodeAt(0);
    return charCode <= 31 || '<>:"/\\|?*'.includes(char) ? '-' : char;
  })
    .join('')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/-+/g, '-')
    .replace(/^[.\s-]+|[.\s-]+$/g, '');

  return cleaned || fallback;
};

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-3.5 w-3.5" />;
    case 'failed':
      return <AlertTriangle className="h-3.5 w-3.5" />;
    case 'uploaded':
    case 'processing':
      return <Spinner size="xs" className="border-blue-500 border-r-transparent" />;
    default:
      return <UploadCloud className="h-3.5 w-3.5" />;
  }
};

function SortableHeader({
  column,
  children,
}: {
  column: { toggleSorting: (desc?: boolean) => void; getIsSorted: () => false | 'asc' | 'desc' };
  children: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-2 h-8 pl-2 pr-0.5 text-xs font-medium text-slate-600"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {children}
      <ArrowUpDown className="h-3.5 w-3.5" />
    </Button>
  );
}

function MultiSelectColumnFilter({
  title,
  options,
  selectedValues,
  onChange,
  open,
  onOpenChange,
  isLoading,
  formatLabel,
}: {
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  formatLabel?: (option: FilterOption) => string;
}) {
  const [query, setQuery] = useState('');
  const selectedSet = new Set(selectedValues);
  const visibleOptions = options.filter((option) => {
    if (!query.trim()) return true;
    return (formatLabel?.(option) ?? option.label).toLowerCase().includes(query.trim().toLowerCase());
  });

  const toggleValue = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selectedValues.filter((selected) => selected !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="relative h-8 w-6 px-0"
        >
          <ListFilter className="h-3.5 w-3.5" />
          {selectedValues.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-900 px-1 text-[10px] text-white">
              {selectedValues.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-2 data-[state=closed]:animate-none data-[state=open]:animate-none"
      >
        <div className="space-y-2">
          <div className="px-1 text-xs font-medium text-slate-600">{title}</div>
          {options.length > 8 && (
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Find ${title.toLowerCase()}...`}
              className="h-8"
            />
          )}
          <div className="max-h-72 min-h-24 overflow-y-auto pr-1">
            {isLoading && options.length === 0 ? (
              <div className="px-2 py-6 text-center text-xs text-slate-500">Loading options...</div>
            ) : visibleOptions.length === 0 ? (
              <div className="px-2 py-6 text-center text-xs text-slate-500">No options</div>
            ) : (
              visibleOptions.map((option) => {
                const label = formatLabel?.(option) ?? option.label;
                return (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-100"
                  >
                    <Checkbox
                      checked={selectedSet.has(option.value)}
                      onCheckedChange={() => toggleValue(option.value)}
                    />
                    <span className="min-w-0 flex-1 truncate" title={label}>{label}</span>
                    <span className="text-xs tabular-nums text-slate-500">{option.count}</span>
                  </label>
                );
              })
            )}
          </div>
          {selectedValues.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-full"
              onClick={() => {
                onChange([]);
                onOpenChange(false);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const Uploads = () => {
  const { slug: matterGuid } = useParams({ from: '/matter/$slug/uploads' });
  const { selectedCase } = useCaseContext();
  const { openDocumentPanel } = useSidePanel();
  const { matter } = useMatter(matterGuid);
  const [filenameQuery, setFilenameQuery] = useState('');
  const [debouncedFilenameQuery, setDebouncedFilenameQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedUploaders, setSelectedUploaders] = useState<string[]>([]);
  const [openFilterPopover, setOpenFilterPopover] = useState<FilterPopoverId | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ]);

  useShellHeader({
    header: 'Uploads',
    subheader: 'Review files uploaded or synced into this matter',
  });

  const sortBy = SORTABLE_COLUMN_IDS.has(sorting[0]?.id) ? sorting[0].id : 'created_at';
  const sortDir = sorting[0]?.desc === false ? 'asc' : 'desc';

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedFilenameQuery(filenameQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [filenameQuery]);

  const buildUploadsPath = useCallback((options: { limit?: number; offset?: number; allResults?: boolean }) => {
    if (!matterGuid) return null;

    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (options.allResults) {
      params.set('all_results', 'true');
    } else {
      params.set('limit', String(options.limit ?? pageSize));
      params.set('offset', String(options.offset ?? 0));
    }

    if (debouncedFilenameQuery.trim()) params.set('filename', debouncedFilenameQuery.trim());
    selectedStatuses.forEach((status) => {
      params.append('statuses', status);
    });
    selectedSources.forEach((source) => {
      params.append('sources', source);
    });
    selectedUploaders.forEach((uploader) => {
      params.append('uploaded_by', uploader);
    });

    return `/correspondence/${matterGuid}/uploads?${params.toString()}`;
  }, [
    matterGuid,
    pageSize,
    sortBy,
    sortDir,
    debouncedFilenameQuery,
    selectedStatuses,
    selectedSources,
    selectedUploaders,
  ]);

  const buildUploadsExportPath = useCallback(() => {
    if (!matterGuid) return null;

    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (debouncedFilenameQuery.trim()) params.set('filename', debouncedFilenameQuery.trim());
    selectedStatuses.forEach((status) => {
      params.append('statuses', status);
    });
    selectedSources.forEach((source) => {
      params.append('sources', source);
    });
    selectedUploaders.forEach((uploader) => {
      params.append('uploaded_by', uploader);
    });

    return `/correspondence/${matterGuid}/uploads/export.csv?${params.toString()}`;
  }, [
    matterGuid,
    sortBy,
    sortDir,
    debouncedFilenameQuery,
    selectedStatuses,
    selectedSources,
    selectedUploaders,
  ]);

  const requestPath = useMemo(
    () => buildUploadsPath({ limit: pageSize, offset: pageIndex * pageSize }),
    [buildUploadsPath, pageIndex, pageSize],
  );
  const filterOptionsPath = useMemo(() => {
    if (!matterGuid) return null;

    const params = new URLSearchParams();
    if (debouncedFilenameQuery.trim()) params.set('filename', debouncedFilenameQuery.trim());
    selectedStatuses.forEach((status) => {
      params.append('statuses', status);
    });
    selectedSources.forEach((source) => {
      params.append('sources', source);
    });
    selectedUploaders.forEach((uploader) => {
      params.append('uploaded_by', uploader);
    });

    const queryString = params.toString();
    return `/correspondence/${matterGuid}/uploads/filter-options${queryString ? `?${queryString}` : ''}`;
  }, [
    matterGuid,
    debouncedFilenameQuery,
    selectedStatuses,
    selectedSources,
    selectedUploaders,
  ]);

  const { data, error, isLoading } = useSWR<MatterUploadsResponse>(
    requestPath,
    async (url) => {
      const response = await backendClient.get<MatterUploadsResponse>(url);
      return response.data;
    },
    { refreshInterval: 10000, revalidateOnFocus: true, keepPreviousData: true },
  );
  const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useSWR<UploadFilterOptionsResponse>(
    filterOptionsPath,
    async (url) => {
      const response = await backendClient.get<UploadFilterOptionsResponse>(url);
      return response.data;
    },
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const uploads = data?.uploads ?? [];
  const filterOptions = filterOptionsData ?? EMPTY_UPLOAD_FILTER_OPTIONS;
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min(total, (pageIndex + 1) * pageSize);

  const resetPage = useCallback(() => setPageIndex(0), []);
  const setFilter = useCallback((setter: (value: string[]) => void) => (values: string[]) => {
    setter(values);
    resetPage();
  }, [resetPage]);
  const filterPopoverProps = useCallback((id: FilterPopoverId) => ({
    open: openFilterPopover === id,
    onOpenChange: (open: boolean) => setOpenFilterPopover(open ? id : null),
  }), [openFilterPopover]);
  const resetFilters = () => {
    setFilenameQuery('');
    setDebouncedFilenameQuery('');
    setSelectedStatuses([]);
    setSelectedSources([]);
    setSelectedUploaders([]);
    resetPage();
  };

  const exportCsv = useCallback(async () => {
    if (!matterGuid || total === 0 || isExporting) return;

    setIsExporting(true);
    setExportProgress(0);
    try {
      const exportPath = buildUploadsExportPath();
      if (!exportPath) return;

      const response = await backendClient.get<Blob>(exportPath, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            setExportProgress(Math.min(95, (event.loaded / event.total) * 100));
            return;
          }
          setExportProgress((current) => Math.min(90, (current ?? 0) + 5));
        },
      });

      const exportDate = new Date().toISOString().slice(0, 10);
      const matterNumber = safeFilenamePart(matter?.matter_no, matterGuid);
      setExportProgress(100);
      downloadBlob(`Upload history - ${matterNumber} - ${exportDate}.csv`, response.data);
    } catch (exportError) {
      console.error('Failed to export uploads CSV:', exportError);
      toast.error(apiErrorMessage(exportError) || 'Unable to export uploads. Please try again.');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  }, [buildUploadsExportPath, isExporting, matter?.matter_no, matterGuid, total]);

  const columns = useMemo<ColumnDef<MatterUpload>[]>(() => [
    {
      id: 'filename',
      accessorFn: (row) => row.display_name || row.file_name,
      header: ({ column }) => <SortableHeader column={column}>Filename</SortableHeader>,
      cell: ({ row }) => {
        const upload = row.original;
        const displayName = upload.display_name || removeTimestampPrefix(upload.file_name);
        const fileReferenceGuid = upload.file_reference_guid;
        const canOpenDocument = upload.processing_status === 'completed' && Boolean(fileReferenceGuid) && Boolean(matterGuid);
        const filename = canOpenDocument ? (
          <Link
            to="/matter/$slug/f/$fileId"
            params={{ slug: matterGuid as string, fileId: fileReferenceGuid as string }}
            search={{ from: 'uploads' }}
            className="block truncate font-medium text-slate-900 hover:text-slate-700 hover:underline"
            title={displayName}
            onClick={(event) => {
              if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && upload.file_reference_guid) {
                event.preventDefault();
                event.stopPropagation();
                openDocumentPanel({
                  documentGuid: upload.file_reference_guid,
                  activeTab: 'document',
                });
              }
            }}
          >
            {displayName}
          </Link>
        ) : (
          <p className="truncate font-medium text-slate-900" title={displayName}>
            {displayName}
          </p>
        );
        return (
          <div className="min-w-[220px] max-w-[360px]">
            {filename}
            {upload.display_name && upload.display_name !== upload.file_name && (
              <p className="truncate text-xs text-slate-500" title={removeTimestampPrefix(upload.file_name)}>
                {removeTimestampPrefix(upload.file_name)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: 'destination_folder',
      accessorKey: 'destination_folder',
      header: ({ column }) => <SortableHeader column={column}>Destination Folder</SortableHeader>,
      cell: ({ row }) => {
        const folder = row.original.destination_folder || 'Root folder';
        return (
          <span className="block max-w-[360px] truncate text-slate-700" title={folder}>
            {folder}
          </span>
        );
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column}>Created At</SortableHeader>,
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-slate-700">
          {formatSmartDate(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'uploaded_by',
      accessorKey: 'uploaded_by_name',
      header: ({ column }) => (
        <div className="flex items-center gap-0.5">
          <SortableHeader column={column}>Uploaded By</SortableHeader>
          <MultiSelectColumnFilter
            title="Uploaded by"
            options={filterOptions.uploaded_by}
            selectedValues={selectedUploaders}
            onChange={setFilter(setSelectedUploaders)}
            {...filterPopoverProps('uploaded_by')}
            isLoading={isLoadingFilterOptions}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="max-w-[220px]">
          <p className="truncate text-slate-900">
            {row.original.uploaded_by_name || 'Unknown user'}
          </p>
        </div>
      ),
    },
    {
      id: 'source',
      accessorKey: 'source_label',
      header: ({ column }) => (
        <div className="flex items-center gap-0.5">
          <SortableHeader column={column}>Source</SortableHeader>
          <MultiSelectColumnFilter
            title="Source"
            options={filterOptions.sources}
            selectedValues={selectedSources}
            onChange={setFilter(setSelectedSources)}
            {...filterPopoverProps('source')}
            isLoading={isLoadingFilterOptions}
          />
        </div>
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-slate-700">{row.original.source_label}</span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'processing_status',
      header: ({ column }) => (
        <div className="flex items-center gap-0.5">
          <SortableHeader column={column}>Status</SortableHeader>
          <MultiSelectColumnFilter
            title="Status"
            options={filterOptions.statuses}
            selectedValues={selectedStatuses}
            onChange={setFilter(setSelectedStatuses)}
            {...filterPopoverProps('status')}
            isLoading={isLoadingFilterOptions}
          />
        </div>
      ),
      cell: ({ row }) => {
        const status = row.original.processing_status;
        const errorMessage = row.original.error_message;
        const badge = (
          <Badge
            variant="outline"
            className={`gap-1.5 whitespace-nowrap ${statusBadgeClassName(status)} ${status === 'failed' && errorMessage ? 'cursor-help' : ''}`}
            title={status === 'failed' && errorMessage ? errorMessage : undefined}
          >
            <StatusIcon status={status} />
            {STATUS_LABELS[status] ?? status}
          </Badge>
        );

        if (status !== 'failed' || !errorMessage) {
          return badge;
        }

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{badge}</TooltipTrigger>
              <TooltipContent side="left" className="max-w-sm whitespace-pre-wrap text-xs">
                {errorMessage}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ], [
    filterOptions,
    selectedStatuses,
    selectedSources,
    selectedUploaders,
    isLoadingFilterOptions,
    filterPopoverProps,
    setFilter,
    matterGuid,
    openDocumentPanel,
  ]);

  const table = useReactTable({
    data: uploads,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      setSorting((current) => {
        const next = typeof updater === 'function' ? updater(current) : updater;
        setPageIndex(0);
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    sortingFns: {
      myCustomSorting: sortingFns.alphanumeric,
    },
  });

  if (typeof selectedCase === 'undefined') {
    return <NoCaseSelected />;
  }

  return (
    <div className="px-6 py-6">
      <div className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="relative xl:w-[360px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={filenameQuery}
            onChange={(event) => {
              setFilenameQuery(event.target.value);
              resetPage();
            }}
            placeholder="Search filenames..."
            className="h-9 pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <div className="relative inline-flex overflow-hidden rounded-md">
            <NewButton
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={total === 0 || isExporting}
              onClick={exportCsv}
              loading={isExporting}
              loadingText="Exporting..."
              icon={Download}
              text="Export CSV"
            />
            {exportProgress !== null && (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-slate-200"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(exportProgress)}
              >
                <div
                  className="h-full bg-blue-500 transition-[width] duration-200 ease-out"
                  style={{ width: `${exportProgress}%` }}
                />
              </div>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={resetFilters}>
            Reset filters
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {apiErrorMessage(error) || 'Unable to load uploads.'}
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16">
          <Loading />
          <span className="text-sm font-medium text-slate-700">Loading uploads...</span>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-md border border-slate-200">
            <Table>
              <TableHeader className="bg-slate-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="hover:bg-transparent">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className="h-10 whitespace-nowrap px-3">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="bg-white">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="px-3 py-1.5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={columns.length} className="h-32 text-center text-sm text-slate-500">
                      No uploads match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-3 flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
            <div>
              Showing {firstRow}-{lastRow} of {total}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPageIndex(0);
                }}
              >
                <SelectTrigger className="h-8 w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50 rows</SelectItem>
                  <SelectItem value="100">100 rows</SelectItem>
                  <SelectItem value="250">250 rows</SelectItem>
                  <SelectItem value="1000">1000 rows</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((page) => Math.max(0, page - 1))}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Previous
              </Button>
              <span className="min-w-20 text-center text-xs">
                Page {pageIndex + 1} of {pageCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={pageIndex + 1 >= pageCount}
                onClick={() => setPageIndex((page) => Math.min(pageCount - 1, page + 1))}
              >
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Uploads;
